import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTemplateFolderSave } from "@/queries/forms/template-folder.query.ts";
import {
  ApiFormTemplateFolder,
  TemplateFolderModule,
} from "@/types/forms/form-template-folder.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface TemplateFolderCreateModalProps extends ModalProps {
  module: TemplateFolderModule;
  folder?: ApiFormTemplateFolder | null;
}

export function TemplateFolderCreateModal({
  module,
  folder,
  ...props
}: TemplateFolderCreateModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;
  const templateFolderSchema = z.object({
    title: z
      .string({ error: t("errors:field_required") })
      .trim()
      .min(1, t("errors:field_required")),
  });
  type TemplateFolderSchema = z.infer<typeof templateFolderSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<TemplateFolderSchema>({
    mode: "onSubmit",
    resolver: zodResolver(templateFolderSchema),
    values: {
      title: folder?.title ?? "",
    },
  });

  const templateFolderSave = useTemplateFolderSave(module, control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await templateFolderSave.mutateAsync({
        id: folder?.id,
        payload: {
          title: data.title.trim(),
        },
      });
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={folder ? t("common:rename_template_folder") : t("core:create_folder")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={templateFolderSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          required={true}
          label={t("common:title")}
          autoFocus={true}
          type={"text"}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
